import { initializeApp, getApps } from 'firebase/app';
import { Capacitor } from '@capacitor/core';
import { httpFetch } from '@/renderer/utils/http';
import { getAuth as fbGetAuth, onAuthStateChanged as fbOnAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut as fbSignOut, GoogleAuthProvider, OAuthProvider, signInWithPopup, signInAnonymously, deleteUser, } from 'firebase/auth';
function readEnv(name) {
    try {
        const fromProcess = globalThis?.process?.env?.[name];
        if (typeof fromProcess === 'string' && fromProcess.length > 0)
            return fromProcess;
        const fromImportMeta = (typeof import.meta !== 'undefined' && import.meta?.env?.[name]);
        if (typeof fromImportMeta === 'string' && fromImportMeta.length > 0)
            return fromImportMeta;
        const fromWindow = globalThis?.__ENV__?.[name];
        if (typeof fromWindow === 'string' && fromWindow.length > 0)
            return fromWindow;
    }
    catch { }
    return undefined;
}
function getFirebaseConfig() {
    const config = {
        apiKey: readEnv('FIREBASE_API_KEY'),
        authDomain: readEnv('FIREBASE_AUTH_DOMAIN'),
        projectId: readEnv('FIREBASE_PROJECT_ID'),
        appId: readEnv('FIREBASE_APP_ID'),
        storageBucket: readEnv('FIREBASE_STORAGE_BUCKET'),
        messagingSenderId: readEnv('FIREBASE_MESSAGING_SENDER_ID'),
    };
    for (const [k, v] of Object.entries(config)) {
        if (!v)
            throw new Error(`Missing Firebase config: ${k}`);
    }
    return config;
}
let app = null;
export function initFirebase() {
    if (app)
        return app;
    if (!getApps().length) {
        app = initializeApp(getFirebaseConfig());
    }
    else {
        app = getApps()[0];
    }
    return app;
}
export function getAuth() {
    return fbGetAuth(initFirebase());
}
export function onAuthStateChanged(cb) {
    const auth = getAuth();
    return fbOnAuthStateChanged(auth, cb);
}
export async function emailPasswordSignIn(email, password) {
    const auth = getAuth();
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
}
export async function emailPasswordRegister(email, password) {
    const auth = getAuth();
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    return cred.user;
}
export async function googleSignIn() {
    // On web, avoid Firebase popup resolver (gapi) due to COOP issues; use Google Identity Services token client
    try {
        const build = globalThis?.__ENV__?.TEXT_GAME_BUILD_PLATFORM;
        const isWeb = !Capacitor?.isNativePlatform?.() && (build === 'web' || typeof window !== 'undefined');
        const webClientId = readEnv('GOOGLE_WEB_CLIENT_ID') || readEnv('GOOGLE_OAUTH_CLIENT_ID');
        if (isWeb && webClientId) {
            const user = await googleSignInViaGIS(webClientId);
            if (user)
                return user;
        }
    }
    catch { }
    // Fallback to default popup (Electron/others)
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    const cred = await signInWithPopup(auth, provider);
    return cred.user;
}
// System-browser Google OAuth (PKCE + loopback) → sign in with credential
export async function googleSystemSignIn() {
    const clientId = readEnv('GOOGLE_OAUTH_CLIENT_ID') || readEnv('FIREBASE_GOOGLE_CLIENT_ID');
    // 1) Native (Android/iOS via Capacitor): use @capacitor/google plugin
    if (Capacitor?.isNativePlatform?.()) {
        // Prefer deep link + system browser (no Play Services dependency)
        try {
            const [{ App }, { Browser }] = await Promise.all([
                import('@capacitor/app'),
                import('@capacitor/browser'),
            ]);
            const oauthClientId = clientId || readEnv('GOOGLE_WEB_CLIENT_ID') || readEnv('FIREBASE_GOOGLE_WEB_CLIENT_ID');
            if (!oauthClientId)
                throw new Error('Missing GOOGLE_WEB_CLIENT_ID');
            // Google installed-app deep link scheme requires client id WITHOUT the domain suffix
            const rawClientId = oauthClientId.replace(/\.apps\.googleusercontent\.com$/i, '');
            const scheme = (readEnv('GOOGLE_OAUTH_REDIRECT_SCHEME') || `com.googleusercontent.apps.${rawClientId}`);
            // Standard Installed App redirect: com.googleusercontent.apps.<client-id>:/oauth2redirect
            const redirectUri = `${scheme}:/oauth2redirect`;
            // PKCE
            const codeVerifier = base64UrlEncode(randBytes(32));
            const codeChallenge = await sha256Base64Url(codeVerifier);
            const state = base64UrlEncode(randBytes(16));
            const scopes = ['openid', 'email', 'profile'].join(' ');
            const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
            authUrl.searchParams.set('client_id', oauthClientId);
            authUrl.searchParams.set('redirect_uri', redirectUri);
            authUrl.searchParams.set('response_type', 'code');
            authUrl.searchParams.set('scope', scopes);
            authUrl.searchParams.set('code_challenge', codeChallenge);
            authUrl.searchParams.set('code_challenge_method', 'S256');
            authUrl.searchParams.set('state', state);
            authUrl.searchParams.set('access_type', 'offline');
            authUrl.searchParams.set('include_granted_scopes', 'true');
            const DEBUG = (readEnv('OAUTH_DEBUG') || '').toLowerCase() === 'true' || globalThis?.process?.env?.NODE_ENV !== 'production';
            if (DEBUG) {
                try {
                    console.log('[oauth] redirectUri', redirectUri);
                }
                catch { }
            }
            const code = await new Promise(async (resolve, reject) => {
                let sub;
                const timeout = setTimeout(async () => {
                    try {
                        await Browser.close();
                    }
                    catch { }
                    if (sub)
                        try {
                            await App.removeAllListeners();
                        }
                        catch { }
                    reject(new Error('OAuth timeout'));
                }, Number(readEnv('GOOGLE_OAUTH_TIMEOUT_MS') || 120000));
                sub = await App.addListener('appUrlOpen', async (data) => {
                    try {
                        const urlStr = data?.url || '';
                        if (DEBUG) {
                            try {
                                console.log('[oauth] appUrlOpen', urlStr);
                            }
                            catch { }
                        }
                        if (!urlStr.startsWith(scheme))
                            return;
                        const url = new URL(urlStr);
                        // Support both scheme:/path?query and scheme://host?query
                        const params = url.searchParams?.size ? url.searchParams : new URLSearchParams(url.hash?.slice(1));
                        const codeParam = params.get('code');
                        const st = params.get('state');
                        if (DEBUG) {
                            try {
                                console.log('[oauth] got code?', !!codeParam, 'state ok?', st === state);
                            }
                            catch { }
                        }
                        if (!codeParam || st !== state)
                            return;
                        clearTimeout(timeout);
                        try {
                            await Browser.close();
                        }
                        catch { }
                        if (sub)
                            try {
                                await App.removeAllListeners();
                            }
                            catch { }
                        resolve(codeParam);
                    }
                    catch (e) {
                        // ignore until valid callback
                    }
                });
                try {
                    if (DEBUG) {
                        try {
                            console.log('[oauth] open', authUrl.toString());
                        }
                        catch { }
                    }
                    await Browser.open({ url: authUrl.toString(), presentationStyle: 'popover' });
                }
                catch (e) {
                    clearTimeout(timeout);
                    if (sub)
                        try {
                            await App.removeAllListeners();
                        }
                        catch { }
                    reject(e);
                }
            });
            // Exchange code → tokens
            const body = new URLSearchParams({
                client_id: oauthClientId,
                code,
                code_verifier: codeVerifier,
                grant_type: 'authorization_code',
                redirect_uri: redirectUri,
            }).toString();
            const tokenResp = await httpFetch('https://oauth2.googleapis.com/token', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body,
                timeoutMs: 20000,
            });
            if (DEBUG) {
                try {
                    console.log('[oauth] token status', tokenResp.status);
                }
                catch { }
            }
            if (!tokenResp.ok) {
                let snippet = '';
                try {
                    snippet = (await tokenResp.text()).slice(0, 200);
                }
                catch { }
                throw new Error(`Token exchange failed: ${tokenResp.status} ${snippet}`);
            }
            const payload = await tokenResp.json();
            const idToken = payload?.id_token;
            const accessToken = payload?.access_token;
            if (!idToken && !accessToken)
                throw new Error('No tokens');
            const auth = getAuth();
            const cred = GoogleAuthProvider.credential(idToken, accessToken);
            let userCred;
            try {
                userCred = await (await import('firebase/auth')).signInWithCredential(auth, cred);
            }
            catch (e) {
                if (DEBUG) {
                    try {
                        console.error('[firebase] signInWithCredential error', e?.code, e?.message);
                    }
                    catch { }
                }
                throw e;
            }
            return userCred.user;
        }
        catch (e) {
            console.warn('Deep link Google auth failed, proceeding to next strategies:', e?.message || e);
        }
    }
    // 2) Desktop (Electron): system browser OAuth via IPC
    if (window?.electron?.ipcRenderer?.invoke) {
        if (!clientId)
            throw new Error('Missing GOOGLE_OAUTH_CLIENT_ID');
        const tokens = await window.electron.ipcRenderer.invoke('auth:google-oauth', {
            clientId,
            scopes: ['openid', 'email', 'profile'],
        });
        const idToken = tokens?.id_token;
        const accessToken = tokens?.access_token;
        if (!idToken && !accessToken)
            throw new Error('No Google tokens');
        const auth = getAuth();
        const cred = GoogleAuthProvider.credential(idToken, accessToken);
        const userCred = await (await import('firebase/auth')).signInWithCredential(auth, cred);
        return userCred.user;
    }
    // 3) Web fallback: popup
    return googleSignIn();
}
// Helpers (renderer)
function randBytes(len) {
    const arr = new Uint8Array(len);
    const g = globalThis.crypto;
    if (g?.getRandomValues)
        g.getRandomValues(arr);
    else
        for (let i = 0; i < len; i++)
            arr[i] = Math.floor(Math.random() * 256);
    return arr;
}
function base64UrlEncode(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i++)
        binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
async function sha256Base64Url(input) {
    const te = new TextEncoder();
    const data = te.encode(input);
    const subtle = globalThis.crypto?.subtle;
    if (!subtle)
        throw new Error('Web Crypto not available');
    const hash = await subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(hash));
}
async function sha256Hex(input) {
    const te = new TextEncoder();
    const data = te.encode(input);
    const subtle = globalThis.crypto?.subtle;
    if (!subtle)
        throw new Error('Web Crypto not available');
    const hash = new Uint8Array(await subtle.digest('SHA-256', data));
    const hex = [];
    for (let i = 0; i < hash.length; i++)
        hex.push(hash[i].toString(16).padStart(2, '0'));
    return hex.join('');
}
export async function signOut() {
    const auth = getAuth();
    await fbSignOut(auth);
}
export async function anonymousSignIn() {
    const auth = getAuth();
    const cred = await signInAnonymously(auth);
    return cred.user;
}
export async function deleteCurrentUserAccount() {
    const auth = getAuth();
    const u = auth.currentUser;
    if (!u)
        throw new Error('Not signed in');
    await deleteUser(u);
}
// Web-only: use Google Identity Services token client to get an access_token,
// then sign in to Firebase with that access token to avoid COOP popup issues
async function googleSignInViaGIS(clientId) {
    await loadGsiClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = globalThis.google;
    if (!g?.accounts?.oauth2?.initTokenClient)
        return null;
    const scope = 'openid email profile';
    const auth = getAuth();
    const accessToken = await new Promise((resolve, reject) => {
        try {
            const client = g.accounts.oauth2.initTokenClient({
                client_id: clientId,
                scope,
                prompt: 'select_account',
                callback: (resp) => {
                    const at = resp?.access_token;
                    if (!at)
                        reject(new Error('No access_token from GIS'));
                    else
                        resolve(at);
                },
            });
            client.requestAccessToken({ prompt: 'select_account' });
        }
        catch (e) {
            reject(e);
        }
    });
    const cred = GoogleAuthProvider.credential(null, accessToken);
    const userCred = await (await import('firebase/auth')).signInWithCredential(auth, cred);
    return userCred.user;
}
async function loadGsiClient() {
    if (globalThis.google?.accounts)
        return;
    await new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://accounts.google.com/gsi/client';
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
        document.head.appendChild(s);
    });
}
// Sign in with Apple (iOS native via Capacitor plugin; web/desktop fallback via popup)
export async function appleSystemSignIn() {
    const auth = getAuth();
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    // Native iOS path via Capacitor plugin
    if (Capacitor?.isNativePlatform?.() && Capacitor?.getPlatform?.() === 'ios') {
        try {
            // Try official plugin first, then community plugin
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let mod = null;
            // Use eval(import()) to avoid bundlers resolving optional native plugins in web builds
            try {
                mod = await (0, eval)('import("@capacitor/apple-sign-in")');
            }
            catch { }
            if (!mod || (!mod.SignInWithApple && !mod?.default?.authorize)) {
                try {
                    mod = await (0, eval)('import("@capacitor-community/apple-sign-in")');
                }
                catch { }
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const plugin = mod?.SignInWithApple || mod?.default || mod;
            if (plugin?.authorize) {
                // Nonce protection per Firebase docs
                const rawNonce = base64UrlEncode(randBytes(32));
                const hashedNonce = await sha256Hex(rawNonce);
                // Some plugins accept nonce in options; set it to bind hash to id_token
                const result = await plugin.authorize({ scopes: ['EMAIL', 'FULL_NAME'], nonce: hashedNonce });
                const idToken = result?.response?.identityToken || result?.identityToken;
                if (!idToken)
                    throw new Error('No Apple identity token');
                const { OAuthProvider: OP, signInWithCredential } = await import('firebase/auth');
                const credential = OP.credential({ idToken, rawNonce });
                const userCred = await signInWithCredential(auth, credential);
                return userCred.user;
            }
        }
        catch (e) {
            try {
                console.warn('[auth] Apple native sign-in failed, falling back to popup:', e?.message || e);
            }
            catch { }
            // continue to popup fallback
        }
    }
    // Web/desktop fallback: popup (requires Apple provider enabled in Firebase)
    const cred = await signInWithPopup(auth, provider);
    return cred.user;
}
